/*
Copyright (C) 2024 Laboratorio Visao Robotica e Imagem

Departamento de Informatica - Universidade Federal do Parana - VRI/UFPR

This file is part of CienciaNaEscola. CienciaNaEscola is free software: you can redistribute it and/or modify it under the terms of the GNU
General Public License as published by the Free Software Foundation, either version 3 of the License, or (at your option) any later version.
CienciaNaEscola is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU General Public License for more details. You should have received a copy
of the GNU General Public License along with CienciaNaEscola.  If not, see <https://www.gnu.org/licenses/>
*/

import { useEffect, useState, useContext } from 'react';
import PerfilImg from '../assets/images/blankProfile.jpg';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Offcanvas } from 'bootstrap';
import { AuthContext } from '../contexts/AuthContext';
import { version } from '../utils/constants';
import { LayoutContext } from '../contexts/LayoutContext';
import { AlertContext } from '../contexts/AlertContext';
import MaterialSymbol from './MaterialSymbol';

const styles = `
    .font-barlow {
        font-family: 'Barlow', sans-serif;
    }

    .font-century-gothic {
        font-family: 'Century Gothic', sans-serif;
    }

    .bg-coral-red {
        background-color: #F59489;
    }

    .bg-steel-blue {
        background-color: #4E9BB9;
    }

    .exit-image {
        max-width: 12px;
        max-height: 12px;
    }

    .profile-image {
        width: 110px;
        height: 110px;
        border: 8px solid #4E9BB9;
        box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.4);
    }

    .sidebar-wrapper h1 {
        opacity: 0.8;
        letter-spacing: 0.05em;
        font-size: 0.7rem !important; /* Texto Menu e Conta menores */
    }

    .nav-link-custom {
        transition: background-color 0.2s ease;
        border-radius: 0;
        color: white !important;
        display: block;
        width: 100%;
        white-space: nowrap;
    }

    .nav-link-custom:hover, 
    .nav-link-custom.active {
        background-color: #FFBCB5;
        text-decoration: none;
    }

    .divider-white {
        border-top: 1px solid rgba(255, 255, 255, 0.25);
        opacity: 1;
    }
`;

/**
 * Componente de barra lateral (Sidebar).
 * @param {Object} props - Propriedades do componente.
 * @param {boolean} props.showExitButton - Define se o botão de saída será exibido.
 */
function Sidebar(props) {
    const { showExitButton = true } = props;
    const { user, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const { isDashboard = false } = useContext(LayoutContext);
    const { showAlert } = useContext(AlertContext);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

    /** Fecha a barra lateral. */
    const closeSidebar = () => {
        const offcanvas = document.getElementById('sidebar');
        const bsOffcanvas = Offcanvas.getOrCreateInstance(offcanvas);
        bsOffcanvas.hide();
    };

    /** Atualiza o estado 'isMobile' conforme a largura da tela muda. */
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 992);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const isActive = (path) => {
        if (!path) return false;
        // Caso especial: se for a home ou dashboard raiz, verificamos igualdade estrita
        if (path === '/dash' || path === '/') {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    return (
        <div className="d-flex flex-column h-100 bg-coral-red overflow-hidden font-barlow">
            {(isMobile || showExitButton) && ( // Se é móvel ou showExitButton está definido como true
                <div className="container d-flex justify-content-end p-0">
                    <button
                        type="button"
                        className="btn btn-transparent rounded-circle p-2 pb-0 m-2 border-0"
                        data-bs-dismiss="offcanvas"
                        data-bs-target="#sidebar"
                    >
                        <MaterialSymbol icon="close" size={22} weight={700} fill color="#FFFFFF" />
                    </button>
                </div>
            )}
            <div className="d-flex flex-column align-items-center py-3">
                <div className="rounded-circle bg-white mb-3">
                    <img
                        className="profile-image rounded-circle"
                        src={user.profileImage ? process.env.REACT_APP_API_URL + 'api/' + user.profileImage : PerfilImg}
                        alt="Perfil"
                    />
                </div>
                <div className="fw-medium">
                    {user.role !== 'GUEST' && user.role !== 'ADMIN' ? (
                        <Link
                            className="px-3 py-1 rounded text-decoration-none text-white bg-steel-blue shadow-sm"
                            to={isDashboard ? '/dash/profile' : '/profile'}
                            onClick={() => closeSidebar()}
                        >
                            {user.username}
                        </Link>
                    ) : (
                        <span className="px-3 py-1 rounded text-white bg-steel-blue shadow-sm">{user.username}</span>
                    )}
                </div>
            </div>
            <hr className="divider-white" />
            <div className="sidebar-wrapper flex-grow-1 overflow-auto scrollbar-none">
                <div className="d-flex flex-column fw-medium pb-4">
                    <h1 className="text-start text-white font-century-gothic fw-bold text-uppercase px-3 mb-2">Menu</h1>
                    <Link
                        className={`nav-link-custom text-decoration-none ps-4 pe-5 py-2 ${
                            isActive(isDashboard ? '/dash/applications' : '/applications') ? 'active' : ''
                        }`}
                        to={isDashboard ? '/dash/applications' : '/applications'}
                        onClick={() => closeSidebar()}
                    >
                        Aplicações
                    </Link>
                    {isDashboard && user.role !== 'USER' && (
                        <Link
                            className={`nav-link-custom text-decoration-none ps-4 pe-5 py-2 ${isActive('/dash/protocols') ? 'active' : ''}`}
                            to="/dash/protocols"
                            onClick={() => closeSidebar()}
                        >
                            Protocolos
                        </Link>
                    )}
                    {isDashboard && isActive('/dash/protocols') && ['MODERATOR', 'ADMIN'].includes(user.role) && (
                        <Link
                            className={`nav-link-custom text-decoration-none ps-4 pe-5 py-2 ${
                                isActive('/dash/protocols/manage') ? 'active' : ''
                            }`}
                            to="/dash/protocols/manage"
                            onClick={() => closeSidebar()}
                        >
                            <div className="d-flex align-items-center">
                                <MaterialSymbol icon="subdirectory_arrow_right" size={22} weight={400} fill color="#FFFFFF" />
                                <span className="ms-2">Curadoria</span>
                            </div>
                        </Link>
                    )}
                    {isDashboard && user.role !== 'USER' && (
                        <Link
                            className={`nav-link-custom text-decoration-none ps-4 pe-5 py-2 ${isActive('/dash/users') ? 'active' : ''}`}
                            to="/dash/users"
                            onClick={() => closeSidebar()}
                        >
                            Usuários e Grupos
                        </Link>
                    )}
                    {isDashboard && user.role !== 'USER' && (
                        <Link
                            className={`nav-link-custom text-decoration-none ps-4 pe-5 py-2 ${
                                isActive('/dash/scienceClubs') ? 'active' : ''
                            }`}
                            to="/dash/scienceClubs"
                            onClick={() => closeSidebar()}
                        >
                            Clubes de Ciência
                        </Link>
                    )}
                    {isDashboard && user.role === 'ADMIN' && (
                        <Link
                            className={`nav-link-custom text-decoration-none ps-4 pe-5 py-2 ${
                                isActive('/dash/institutions') ? 'active' : ''
                            }`}
                            to="/dash/institutions"
                            onClick={() => closeSidebar()}
                        >
                            Instituições
                        </Link>
                    )}
                    {isDashboard && user.role !== 'USER' && user.role !== 'ADMIN' && user.institutionId && (
                        <Link
                            className={`nav-link-custom text-decoration-none ps-4 pe-5 py-2 ${
                                isActive(`/dash/institutions/${user.institutionId}`) ? 'active' : ''
                            }`}
                            to={`/dash/institutions/${user.institutionId}`}
                            onClick={() => closeSidebar()}
                        >
                            Minha Instituição
                        </Link>
                    )}
                    <Link
                        className={`nav-link-custom text-decoration-none ps-4 pe-5 py-2 ${
                            isActive(isDashboard ? '/dash/about' : '/about') ? 'active' : ''
                        }`}
                        to={isDashboard ? '/dash/about' : '/about'}
                        onClick={() => closeSidebar()}
                    >
                        Sobre o App
                    </Link>
                    <Link
                        className={`nav-link-custom text-decoration-none ps-4 pe-5 py-2 ${
                            isActive(isDashboard ? '/dash/terms' : '/terms') ? 'active' : ''
                        }`}
                        to={isDashboard ? '/dash/terms' : '/terms'}
                        onClick={() => closeSidebar()}
                    >
                        Termos de Uso
                    </Link>
                </div>
                {user.role !== 'GUEST' && user.role !== 'ADMIN' && (
                    <div className="d-flex flex-column fw-medium pb-4">
                        <h1 className="text-start text-white font-century-gothic fw-bold text-uppercase px-3 mb-2">Conta</h1>
                        <Link
                            className={`nav-link-custom text-decoration-none ps-4 pe-5 py-2 ${
                                isActive(isDashboard ? '/dash/profile' : '/profile') ? 'active' : ''
                            }`}
                            to={isDashboard ? '/dash/profile' : '/profile'}
                            onClick={() => closeSidebar()}
                        >
                            Perfil
                        </Link>
                    </div>
                )}
            </div>
            <div className="mt-auto d-flex flex-column">
                <hr className="divider-white my-1" />
                <button
                    className="nav-link-custom btn text-start text-white text-decoration-none rounded-0 fw-medium ps-4 pe-5 py-2 mb-2 border-0 shadow-none"
                    type="button"
                    onClick={() => {
                        showAlert({
                            headerText: 'Tem certeza de que deseja sair?',
                            primaryBtnHsl: [355, 78, 66],
                            primaryBtnLabel: 'Não',
                            secondaryBtnHsl: [97, 43, 70],
                            secondaryBtnLabel: 'Sim',
                            onSecondaryBtnClick: () => {
                                closeSidebar();
                                logout();
                                navigate(isDashboard ? '/dash/signin' : '/signin');
                            },
                        });
                    }}
                >
                    <div className="d-flex align-items-center">
                        <MaterialSymbol icon="logout" size={22} weight={400} fill color="#FFFFFF" />
                        <span className="ms-2">Sair</span>
                    </div>
                </button>
                <div className="ps-4 pb-3">
                    <p className="text-white opacity-70 fw-medium mb-0" style={{ fontSize: '0.75rem' }}>
                        {version}
                    </p>
                </div>
            </div>
            <style>{styles}</style>
        </div>
    );
}

export default Sidebar;
